import {
  ArrowUpRight,
  ChartBar,
  ChartBarIcon,
  HandCoins,
  Megaphone,
  Money,
  TrendUp,
  X,
} from "@phosphor-icons/react";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { Button } from "flowbite-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BannerNote } from "../../../../components/BannerNote";
import { Table } from "../../../../components/Table";
import { useDateRange } from "../../../../hooks";
import { useIsMobile } from "../../../../hooks";
import { useMetaAdsCampaigns } from "../../../../hooks/get/useMetaAdsCampaigns";
import { IDateRangePreset } from "../../../../interfaces";
import { cn, getAmountByCurrency, getCurrencySymbol } from "../../../../lib/utils";
import { CurrencyCode, DEFAULT_ITEMS_PER_PAGE, LOCAL_STORAGE_KEYS } from "../../../../shared/constants";
import AppLogo from "../../../AppCountryReports/components/AppLogo";
import BaseCampaignList from "../../components/shared/BaseCampaignList";
import CampaignPerformanceDrawer from "../../components/shared/CampaignPerformanceDrawer";
import { IMetaCampaign } from "../../interface/metaAds";

const MetaAdsCampaignList: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const currency = "USD"; // Default display currency for aggregate metrics
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [selectedStatus, setSelectedStatus] = useState<{ label: string; value: string } | null>(null);
  const [selectedCampaignForMetrics, setSelectedCampaignForMetrics] = useState<IMetaCampaign | null>(null);

  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [perPage, setPerPage] = useState(Number(searchParams.get("per_page")) || DEFAULT_ITEMS_PER_PAGE);
  const [sortBy, setSortBy] = useState(searchParams.get("sort_by") || "");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">((searchParams.get("sort_order") as "asc" | "desc") || "desc");

  const { dateRange, selectedPreset, setDateRange, setSelectedPreset } = useDateRange();

  const handleDateRangeChange = useCallback((startDate: Date, endDate: Date) => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_FROM, startDate.toISOString());
    localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_TO, endDate.toISOString());
    setDateRange(startDate, endDate);
  }, [setDateRange]);

  const handlePresetChange = useCallback((preset: IDateRangePreset | undefined) => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET, preset?.id || "");
    setSelectedPreset(preset);
  }, [setSelectedPreset]);

  const { data: campaignsData, isLoading, isFetching } = useMetaAdsCampaigns({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    search: searchTerm,
    status: selectedStatus?.value,
    page,
    per_page: perPage,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const campaigns = useMemo(() => campaignsData?.campaigns || [], [campaignsData]);

  // Sync state from URL on mount
  useEffect(() => {
    const urlStatus = searchParams.get("status");
    if (urlStatus) {
      const status = [
        { label: "Active", value: "ACTIVE" },
        { label: "Paused", value: "PAUSED" },
        { label: "Archived", value: "ARCHIVED" },
      ].find((s) => s.value === urlStatus);
      if (status) setSelectedStatus(status);
    }
  }, [searchParams]);

  const updateUrlParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value && value !== "all") {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setPage(1);
    updateUrlParams({ search: value || null, page: "1" });
  }, [updateUrlParams]);

  const metrics = useMemo(() => {
    const totalMetrics = campaignsData?.total_metrics;
    const metricsCurrency = (totalMetrics?.currency || "USD") as string;

    return {
      totalSpend: getAmountByCurrency(totalMetrics as Record<string, any> || {}, "spend", metricsCurrency),
      totalCampaigns: campaignsData?.pagination?.total_count || 0,
      totalInstalls: totalMetrics?.installs || 0,
      totalImpressions: totalMetrics?.impressions || 0,
      totalClicks: totalMetrics?.clicks || 0,
      avgCtr: totalMetrics?.ctr || 0,
      avgCpc: totalMetrics?.cpa || 0,
      // totalRevenue: (totalMetrics?.spend || 0) * 2.85, // Keeping some logic if revenue not in metrics
      avgRoas: totalMetrics?.conversion_rate || 0,
    };
  }, [campaignsData]);

  const handleFilterApply = useCallback((values: any) => {
    const status = values.status as { label: string; value: string } | null;
    setSelectedStatus(status);
    setPage(1);
    updateUrlParams({ status: status?.value || null, page: "1" });
  }, [updateUrlParams]);

  const activeFilters = useMemo(() => {
    const filters: any[] = [];
    if (selectedStatus && selectedStatus.value !== "all") {
      filters.push({
        id: "status",
        label: selectedStatus.label,
        onClear: () => {
          setSelectedStatus(null);
          setPage(1);
          updateUrlParams({ status: null, page: "1" });
        },
      });
    }
    return filters;
  }, [selectedStatus, updateUrlParams]);

  const handleClearAllFilters = useCallback(() => {
    setSelectedStatus(null);
    setPage(1);
    updateUrlParams({ status: null, page: "1" });
  }, [updateUrlParams]);

  const handleSort = useCallback((sorting: any) => {
    if (sorting.length > 0) {
      const newSortBy = sorting[0].id;
      const newSortOrder = sorting[0].desc ? "desc" : "asc";
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      setPage(1);
      updateUrlParams({ sort_by: newSortBy, sort_order: newSortOrder, page: "1" });
    } else {
      setSortBy("");
      setSortOrder("desc");
      updateUrlParams({ sort_by: null, sort_order: null });
    }
  }, [updateUrlParams]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    updateUrlParams({ page: newPage.toString() });
  }, [updateUrlParams]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPerPage(newSize);
    setPage(1);
    updateUrlParams({ per_page: newSize.toString(), page: "1" });
  }, [updateUrlParams]);

  const columns = useMemo<ColumnDef<IMetaCampaign>[]>(
    () => [
      {
        accessorKey: "campaign_name",
        header: t("campaigns.columns.campaign", "Campaign"),
        size: 250,
        minSize: 200,
        maxSize: 300,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => {
          const campaign = row.original;
          const platform = "meta_ads"
          return (
            <div className="flex items-center gap-3 py-1">
              <div className="flex-shrink-0 w-8 h-8 flex justify-center items-center border border-gray-100 dark:border-neutral-800 rounded-lg bg-gray-50 dark:bg-neutral-900/50 shadow-sm overflow-hidden p-1">
                <AppLogo
                  key={`${campaign.app_logo}-${campaign.app_id}`}
                  logoUrl={campaign.app_logo || null}
                  platform={platform}
                  appName={campaign.app_name || campaign.campaign_name || ""}
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate" title={campaign.campaign_name}>
                {campaign.app_name || campaign.campaign_name || ""}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "objective",
        header: t("campaigns.columns.objective", "Objective"),
        size: 150,
        enableSorting: false,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => {
          const objective = row.original.objective || "OUTCOME_APP_PROMOTION";
          return (
            <span className="text-sm text-gray-500 capitalize">
              {objective.toLowerCase().replace(/_/g, " ")}
            </span>
          );
        }
      },
      {
        accessorKey: "daily_budget",
        header: t("campaigns.columns.budget", "Budget"),
        size: 120,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => {
          const campaign = row.original;
          const rowCurrency = (campaign.currency || currency) as CurrencyCode;
          const budget = campaign.daily_budget;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                {budget != null ? `${getCurrencySymbol(rowCurrency)}${budget.toLocaleString()}` : "N/A"}
              </span>
              <span className="text-[10px] text-gray-400 uppercase">Daily</span>
            </div>
          );
        },
      },
      {
        accessorKey: "spend",
        header: t("campaigns.columns.spend", "Spend"),
        size: 100,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => {
          const campaign = row.original;
          const rowCurrency = (campaign.currency || currency) as CurrencyCode;
          return (
            <span className="text-sm text-green-600 dark:text-green-400 font-semibold">
              {getCurrencySymbol(rowCurrency)}
              {getAmountByCurrency(campaign as Record<string, any>, "spend", rowCurrency).toLocaleString()}
            </span>
          );
        }
      },
      {
        accessorKey: "impressions",
        header: t("campaigns.columns.impressions", "Impressions"),
        size: 100,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">{(row.original.impressions || 0).toLocaleString()}</span>
        )
      },
      {
        accessorKey: "clicks",
        header: t("campaigns.columns.clicks", "Clicks"),
        size: 100,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">{(row.original.clicks || 0).toLocaleString()}</span>
        )
      },
      {
        accessorKey: "ctr",
        header: t("campaigns.columns.ctr", "CTR"),
        size: 80,
        enableSorting: false,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">{(row.original.ctr || 0).toFixed(2)}%</span>
        )
      },
      {
        accessorKey: "installs",
        header: t("campaigns.columns.installs", "Installs"),
        size: 80,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">{(row.original.installs || 0).toLocaleString()}</span>
        )
      },
      {
        accessorKey: "status",
        header: t("campaigns.columns.status", "Status"),
        size: 100,
        enableSorting: false,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => {
          const status = row.original.status || row.original.meta_campaign_status;
          const isEnabled = status === "ACTIVE" || status === "ENABLED";

          return (
            <div className={cn(
              "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider",
              isEnabled ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-100 dark:border-green-800/50" : "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-100 dark:border-yellow-800/50"
            )}>
              <div className={cn("w-1.5 h-1.5 rounded-full", isEnabled ? "bg-green-500" : "bg-yellow-500")} />
              {isEnabled ? t("common.enabled") : t("common.paused")}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: t("campaigns.columns.actions", "Actions"),
        size: 120,
        enableSorting: false,
        cell: ({ row }: CellContext<IMetaCampaign, unknown>) => {
          return (
            <div className="flex items-center gap-2">
              <Button
                color="light"
                size={"xs"}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCampaignForMetrics(row.original);
                }}
              >
                <ChartBarIcon size={20} />
              </Button>
            </div>
          );
        },
      },
    ],
    [t, currency, campaignsData],
  );

  const headerProps = useMemo(() => ({
    title: `Meta Ads ${t("common.campaignsManagement")}`,
    platform: "meta_ads" as const,
    type: "list" as const,
    onSearch: handleSearch,
    searchTerm: searchTerm,
    onFilterApply: handleFilterApply,
    filterFields: [
      {
        id: "status",
        label: "Status",
        type: "select",
        placeholder: "Any Status",
        value: selectedStatus,
        options: [
          { label: "Active", value: "ACTIVE" },
          { label: "Paused", value: "PAUSED" },
          { label: "Archived", value: "ARCHIVED" },
        ],
      },
    ],
    activeFilters: activeFilters,
    onClearAllFilters: handleClearAllFilters,
    onDateRangeChange: handleDateRangeChange,
    onPresetChange: handlePresetChange,
    dateRange: dateRange,
    selectedPresetId: selectedPreset?.id,
  }), [t, handleSearch, searchTerm, handleFilterApply, selectedStatus, activeFilters, handleClearAllFilters, handleDateRangeChange, handlePresetChange, dateRange, selectedPreset]);

  const stats = useMemo(() => [
    {
      title: t("campaigns.metrics.activeCampaigns", "Active Campaigns"),
      value: metrics.totalCampaigns,
      icon: <Megaphone size={20} weight="duotone" />,
    },
    {
      title: t("campaigns.metrics.totalSpend", "Total Spend"),
      value: metrics.totalSpend,
      isCurrency: true,
      icon: <Money size={20} weight="duotone" />,
    },
    // {
    //   title: t("campaigns.metrics.impressions", "Impressions"),
    //   value: metrics.totalImpressions,
    //   icon: <ChartBar size={20} weight="duotone" />,
    // },
    // {
    //   title: t("campaigns.metrics.clicks", "Clicks"),
    //   value: metrics.totalClicks,
    //   icon: <TrendUp size={20} weight="duotone" />,
    // },
    {
      title: t("campaigns.metrics.avgCtr", "Avg. CTR"),
      value: metrics.avgCtr,
      isPercentage: true,
      icon: <ArrowUpRight size={20} weight="duotone" />,
    },
    {
      title: t("campaigns.metrics.avgCpc", "Avg. CPC"),
      value: metrics.avgCpc,
      isCurrency: true,
      icon: <HandCoins size={20} weight="duotone" />,
    },
    {
      title: t("campaigns.metrics.totalInstalls", "Total Installs"),
      value: metrics.totalInstalls,
      icon: <TrendUp size={20} weight="duotone" />,
    },
    // {
    //   title: t("campaigns.metrics.totalRevenue", "Total Revenue"),
    //   value: metrics.totalRevenue,
    //   isCurrency: true,
    //   icon: <HandCoins size={20} weight="duotone" />,
    // },
  ], [t, metrics]);

  return (
    <BaseCampaignList
      headerProps={headerProps}
      statsColumnsCount={5}
      stats={stats}
      isLoading={isLoading}
      currency={"USD"}
      banner={
        <BannerNote
          note={t("campaigns.info.metricsUpdateDelay", "Metrics are updated every 24 hours. Some data points might have a slight delay depending on the platform provider.")}
          title={t("common.dataSourceInfo")}
          marqueeSpeed={25}
          delay={3}
        />
      }
    >
      <Table
        data={campaigns}
        columns={columns}
        isLoading={isFetching}
        loadingHeight={4}
        loadingRowCount={5}
        onRowClick={(campaign) => {
          navigate(`/campaigns/meta-ads/${(campaign as IMetaCampaign).campaign_id}`);
        }}
        // maxHeight={`calc(100vh - ${(isMobile ? 331 : isLoading ? 271 : 331) + BannerHeightOffset}px)`}
        maxHeight={`h-full`}
        isMobile={isMobile}
        manualPagination={true}
        manualSorting={true}
        pageCount={campaignsData?.pagination?.total_pages || 0}
        totalItemsCount={campaignsData?.pagination?.total_count || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
      />

      <CampaignPerformanceDrawer
        isOpen={!!selectedCampaignForMetrics}
        onClose={() => setSelectedCampaignForMetrics(null)}
        campaignName={selectedCampaignForMetrics?.campaign_name || ""}
        platform="meta_ads"
        status={selectedCampaignForMetrics?.status || ""}
        settings={[
          { label: t("common.columns.campaignId", "Campaign ID"), value: selectedCampaignForMetrics?.campaign_id || selectedCampaignForMetrics?.meta_campaign_id || "" },
          { label: t("campaigns.detail.objective", "Objective"), value: (selectedCampaignForMetrics?.objective || selectedCampaignForMetrics?.meta_campaign_objective || "OUTCOME_APP_PROMOTION").toLowerCase().replace(/_/g, " ") },
          {
            label: t("campaigns.detail.dailyBudget", "Daily Budget"),
            value: (() => {
              const budget = selectedCampaignForMetrics?.daily_budget;
              const curr = (selectedCampaignForMetrics?.currency || selectedCampaignForMetrics?.default_currency || "USD") as CurrencyCode;
              return budget != null ? `${getCurrencySymbol(curr)}${budget.toLocaleString()}` : "-";
            })()
          },
          { label: t("campaigns.detail.bidStrategy", "Bid Strategy"), value: (selectedCampaignForMetrics?.bid_strategy || selectedCampaignForMetrics?.meta_bid_strategy || "N/A").replace(/_/g, " ") },
          { label: t("campaigns.detail.buyingType", "Buying Type"), value: (selectedCampaignForMetrics?.buying_type || selectedCampaignForMetrics?.meta_buying_type || "AUCTION").replace(/_/g, " ") },
        ]}
        performanceMetrics={[
          { label: "Spend", value: `${getCurrencySymbol((selectedCampaignForMetrics?.currency || "USD") as CurrencyCode)}${getAmountByCurrency(selectedCampaignForMetrics as Record<string, any> || {}, "spend", selectedCampaignForMetrics?.currency || "USD").toLocaleString()}` },
          { label: "Impressions", value: (selectedCampaignForMetrics?.impressions || 0).toLocaleString() },
          { label: "Clicks", value: (selectedCampaignForMetrics?.clicks || 0).toLocaleString() },
          { label: "CTR", value: `${(selectedCampaignForMetrics?.ctr || 0).toFixed(2)}%` },
          { label: "Installs", value: (selectedCampaignForMetrics?.installs || 0).toLocaleString() },
          { label: "Conversions", value: (selectedCampaignForMetrics?.conversions || 0).toLocaleString() },
          { label: "CPA", value: `$${(selectedCampaignForMetrics?.cpa || 0).toFixed(2)}` },
          { label: "CPM", value: `$${(selectedCampaignForMetrics?.cpm || 0).toFixed(2)}` },
        ]}
        funnelData={selectedCampaignForMetrics ? {
          impressions: selectedCampaignForMetrics?.impressions || 0,
          clicks: selectedCampaignForMetrics?.clicks || 0,
          installs: selectedCampaignForMetrics?.installs || 0,
          purchases: 0,
        } : undefined}
        metadata={[
          { label: "Created", value: selectedCampaignForMetrics?.created_at ? dayjs(selectedCampaignForMetrics.created_at).format("MMM DD, YYYY") : "" },
        ]}
        footerActions={
          <>
            <Button color="light" onClick={() => setSelectedCampaignForMetrics(null)} className="w-full">
              <div className="flex items-center gap-2">
                <X size={18} />
                Close
              </div>
            </Button>
            <Button
              color="primary"
              className="w-full"
              onClick={() => {
                navigate(`/campaigns/meta-ads/${selectedCampaignForMetrics?.campaign_id}`);
                setSelectedCampaignForMetrics(null);
              }}
            >
              <div className="flex items-center gap-2">
                View Detailed Report
                <ArrowUpRight size={18} />
              </div>
            </Button>
          </>
        }
      />
    </BaseCampaignList>
  );
};

export default MetaAdsCampaignList;
